import { AnimatePresence, motion } from "motion/react";
import { CheckCircle2, Sparkles, XCircle } from "lucide-react";
import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import { spring } from "@/lib/motion";

/** A button inside the toast ("Aplicar"): tapping it runs `onClick` and closes the toast. */
export type ToastAction = { label: string; onClick: () => void };
type Tone = "success" | "error" | "question";
type Toast = { id: number; message: string; tone: Tone; action?: ToastAction };
type Push = (message: string, tone?: Tone, action?: ToastAction) => void;
const ToastContext = createContext<Push>(() => {});

export const useToast = () => useContext(ToastContext);

// long enough to read a question and reach its button with the thumb
const ACTION_MS = 6000;
const PLAIN_MS = 2800;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const close = useCallback((id: number) => setToasts((t) => t.filter((x) => x.id !== id)), []);
  const push = useCallback<Push>(
    (message, tone = "success", action) => {
      const id = Date.now() + Math.random();
      setToasts((t) => [...t, { id, message, tone, action }]);
      setTimeout(() => close(id), action ? ACTION_MS : PLAIN_MS);
    },
    [close],
  );
  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex flex-col items-center gap-2 px-4 pt-[calc(env(safe-area-inset-top)+12px)]"
        aria-live="polite"
      >
        <AnimatePresence>
          {toasts.map((t) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -12, scale: 0.97 }}
              transition={spring.snappy}
              className="flex max-w-full items-center gap-2 rounded-[22px] px-4 py-2.5 text-subhead font-medium glass"
            >
              {t.tone === "success" ? (
                <CheckCircle2 className="size-5 shrink-0 text-positive" aria-hidden />
              ) : t.tone === "question" ? (
                <Sparkles className="size-5 shrink-0 text-accent" aria-hidden />
              ) : (
                <XCircle className="size-5 shrink-0 text-negative" aria-hidden />
              )}
              <span className="min-w-0">{t.message}</span>
              {t.action && (
                <button
                  type="button"
                  className="pointer-events-auto -my-1.5 -mr-2 min-h-9 shrink-0 rounded-full bg-accent px-3 text-subhead font-semibold text-accent-contrast"
                  onClick={() => {
                    t.action?.onClick();
                    close(t.id);
                  }}
                >
                  {t.action.label}
                </button>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </ToastContext.Provider>
  );
}
